/**
 * SegmentEditor - Main component for the Extreme Action edit step
 * Mini video editor with timeline, trim, speed, and reorder
 */
import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Play, Pause, SkipBack, SkipForward, ChevronLeft, ChevronRight,
  ZoomIn, ZoomOut, Undo2, Redo2, Sparkles, Loader2, Clock
} from 'lucide-react';

import { SegmentTimeline } from './components/SegmentTimeline';
import { useSegmentPlayback } from './hooks/useSegmentPlayback';
import { useSegmentVideoPlayer } from './hooks/useSegmentVideoPlayer';
import { 
  getEffectiveDuration, 
  calculateSegmentPositions, 
  formatTimecode, 
  formatDuration 
} from './utils/segmentUtils';

export function SegmentEditor({
  initialSegments,
  speedMultiplier,
  onSpeedChange,
  onCompile,
  compiling,
  getAssetUrl
}) {
  // Editor state
  const [segments, setSegments] = useState(() => 
    calculateSegmentPositions(initialSegments.map((s, i) => ({
      ...s,
      id: s.id || `segment_${i}`,
      order: s.order ?? i,
      duration_ms: s.duration_ms || 5000,
      trim_start_ms: s.trim_start_ms || 0,
      trim_end_ms: s.trim_end_ms || 0,
      speed: s.speed ?? 1,
      video_url: s.video_url
    })))
  );
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  
  // History for undo/redo - initialize with current segments
  const [history, setHistory] = useState(() => [JSON.stringify(segments)]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // Refs
  const videoRef = useRef(null);
  const playheadRef = useRef(null);
  const timecodeRef = useRef(null);

  // Calculate total duration
  const totalDuration = useMemo(() => 
    segments.reduce((sum, seg) => sum + getEffectiveDuration(seg), 0),
    [segments]
  );

  // Playback controls
  const {
    isPlaying,
    playheadPosition,
    play,
    pause,
    togglePlayPause,
    seek,
    stepForward,
    stepBackward,
    goToStart,
    goToEnd,
    getSegmentAtPosition
  } = useSegmentPlayback(segments, totalDuration);

  // Update playhead directly for smooth animation
  const updatePlayheadDirect = useCallback((positionMs) => {
    if (playheadRef.current?.setPosition) {
      playheadRef.current.setPosition(positionMs);
    }
    if (timecodeRef.current) {
      timecodeRef.current.textContent = formatTimecode(positionMs);
    }
  }, []);

  // Video player
  const {
    currentSegment,
    isLoading,
    allPreloaded,
    seekToPosition
  } = useSegmentVideoPlayer({
    videoRef,
    segments,
    getAssetUrl,
    isPlaying,
    onPositionChange: updatePlayheadDirect,
    onSeekComplete: seek,
    onSegmentChange: (seg) => console.log('Playing segment:', seg?.id)
  });

  // Seek video when scrubbing (not during playback)
  useEffect(() => {
    if (!isPlaying && seekToPosition) {
      seekToPosition(playheadPosition);
    }
  }, [playheadPosition, isPlaying, seekToPosition]);

  // Save state to history
  const saveToHistory = useCallback((newSegments) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newSegments));
    // Limit history size
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Update a segment
  const handleUpdateSegment = useCallback((segmentId, updates) => {
    setSegments(prev => {
      const newSegments = prev.map(seg => 
        seg.id === segmentId ? { ...seg, ...updates } : seg
      );
      const recalculated = calculateSegmentPositions(newSegments);
      saveToHistory(recalculated);
      return recalculated;
    });
  }, [saveToHistory]);

  // Reorder segments
  const handleReorderSegments = useCallback((fromIndex, toIndex) => {
    setSegments(prev => {
      const newSegments = [...prev];
      const [moved] = newSegments.splice(fromIndex, 1);
      newSegments.splice(toIndex, 0, moved);
      // Update order property
      const reordered = newSegments.map((seg, i) => ({ ...seg, order: i }));
      const recalculated = calculateSegmentPositions(reordered);
      saveToHistory(recalculated);
      return recalculated;
    });
  }, [saveToHistory]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const restored = JSON.parse(history[newIndex]);
      setSegments(calculateSegmentPositions(restored));
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const restored = JSON.parse(history[newIndex]);
      setSegments(calculateSegmentPositions(restored));
    }
  }, [history, historyIndex]);


  // Get selected segment
  const selectedSegment = useMemo(() => 
    segments.find(s => s.id === selectedSegmentId),
    [segments, selectedSegmentId]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (e.shiftKey) stepBackward();
          else seek(playheadPosition - 1000);
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (e.shiftKey) stepForward();
          else seek(playheadPosition + 1000);
          break;
        case 'Home':
          e.preventDefault();
          goToStart();
          break;
        case 'End':
          e.preventDefault();
          goToEnd();
          break;
        case 'KeyZ':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            if (e.shiftKey) handleRedo();
            else handleUndo();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlayPause, stepBackward, stepForward, seek, playheadPosition, goToStart, goToEnd, handleUndo, handleRedo]);

  return (
    <div className="flex flex-col h-full bg-zinc-900 rounded-lg overflow-hidden">
      {/* Video Preview */}
      <div className="relative bg-black flex-shrink-0" style={{ height: '300px' }}>
        <video
          ref={videoRef}
          className="w-full h-full object-contain"
          playsInline
        />
        
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        )}
        
        {/* Preload status */}
        {!allPreloaded && (
          <div className="absolute top-2 right-2">
            <Badge variant="outline" className="text-xs text-amber-400 border-amber-500/50">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Preloading...
            </Badge>
          </div>
        )}
      </div>

      {/* Playback Controls */}
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-800 border-b border-zinc-700">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={goToStart} className="h-8 w-8 text-zinc-400 hover:text-white">
            <SkipBack className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={stepBackward} className="h-8 w-8 text-zinc-400 hover:text-white">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={togglePlayPause}
            className="h-10 w-10 text-white hover:bg-orange-500/20"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={stepForward} className="h-8 w-8 text-zinc-400 hover:text-white">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={goToEnd} className="h-8 w-8 text-zinc-400 hover:text-white">
            <SkipForward className="w-4 h-4" />
          </Button>
        </div>

        {/* Timecode */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-zinc-500" />
            <span ref={timecodeRef} className="text-sm font-mono text-zinc-300">
              {formatTimecode(playheadPosition)}
            </span>
            <span className="text-zinc-500">/</span>
            <span className="text-sm font-mono text-zinc-500">
              {formatTimecode(totalDuration)}
            </span>
          </div>
        </div>

        {/* Zoom & History */}
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className="h-8 w-8 text-zinc-400 hover:text-white disabled:opacity-30"
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className="h-8 w-8 text-zinc-400 hover:text-white disabled:opacity-30"
          >
            <Redo2 className="w-4 h-4" />
          </Button>
          
          <div className="w-px h-6 bg-zinc-700 mx-2" />
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setZoomLevel(z => Math.max(0.1, z - 0.2))}
            className="h-8 w-8 text-zinc-400 hover:text-white"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs text-zinc-400 w-12 text-center">
            {Math.round(zoomLevel * 100)}%
          </span>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setZoomLevel(z => Math.min(30, z + 0.2))}
            className="h-8 w-8 text-zinc-400 hover:text-white"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-shrink-0">
        <SegmentTimeline
          segments={segments}
          totalDuration={totalDuration}
          zoomLevel={zoomLevel}
          playheadPosition={playheadPosition}
          selectedSegmentId={selectedSegmentId}
          onSelectSegment={setSelectedSegmentId}
          onUpdateSegment={handleUpdateSegment}
          onReorderSegments={handleReorderSegments}
          onSeek={seek}
          getAssetUrl={getAssetUrl}
          playheadRef={playheadRef}
        />
      </div>

      {/* Selected Segment Settings */}
      <div className="p-4 bg-zinc-800/50 border-t border-zinc-700">
        {selectedSegment ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-zinc-300">
                Segment {segments.findIndex(s => s.id === selectedSegmentId) + 1}
              </span>
              <Badge variant="outline" className="text-xs text-zinc-400">
                Original: {formatDuration(selectedSegment.duration_ms || 5000)}
              </Badge>
            </div>
            
            {/* Speed control */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-zinc-400">Segment Speed</Label>
                <span className="text-sm font-bold text-orange-400">
                  {(selectedSegment.speed ?? 1).toFixed(2)}x
                </span>
              </div>
              <Slider
                value={[selectedSegment.speed ?? 1]}
                onValueChange={([v]) => handleUpdateSegment(selectedSegmentId, { speed: v })}
                min={0.25}
                max={10}
                step={0.25}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-zinc-500">
                <span>0.25x</span>
                <span>1x</span>
                <span>10x</span>
              </div>
            </div>
            
            {/* Trim info */}
            <div className="flex items-center gap-4 text-xs text-zinc-400">
              <span>Trim In: {formatDuration(selectedSegment.trim_start_ms || 0)}</span>
              <span>Trim Out: {formatDuration(selectedSegment.trim_end_ms || 0)}</span>
              <span className="ml-auto text-zinc-300">
                Effective: {formatDuration(getEffectiveDuration(selectedSegment))}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-zinc-500 text-center">
            Select a segment to adjust speed and trim
          </p>
        )}
      </div>

      {/* Compile Controls */}
      <div className="p-4 bg-zinc-900 border-t border-zinc-700 space-y-3">
        {/* Global speed control */}
        <div className="flex items-center gap-4">
          <Label className="text-sm text-zinc-300 shrink-0">Final Speed:</Label>
          <Slider
            value={[speedMultiplier]}
            onValueChange={([v]) => onSpeedChange(v)}
            min={0.5}
            max={10}
            step={0.5}
            className="flex-1"
          />
          <span className="text-lg font-bold text-orange-400 w-16 text-right">
            {speedMultiplier}X
          </span>
        </div>
        
        {/* Compile button */}
        <Button
          onClick={() => onCompile(segments)}
          disabled={compiling}
          className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium py-3"
        >
          {compiling ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Compiling...</>
          ) : (
            <><Sparkles className="w-4 h-4 mr-2" /> Compile at {speedMultiplier}X Speed</>
          )}
        </Button>
      </div>
    </div>
  );
}

export default SegmentEditor;
